import { useEffect, useRef } from 'react';
import { usePlanetStore } from '../store/planetStore';
import { usePlayerStore } from '../store/playerStore';
import { useFleetStore } from '../store/fleetStore';
import { HEARTBEAT_MS } from '../config/gameLoop';
import {
    earliestDueMs,
    fleetDueTimesMs,
    planetDueTimesMs,
    researchDueTimesMs,
    shipUpgradeDueTimesMs,
} from '../utils/dueCheckpoints';

/** How often to check whether anything queued has come due. */
const SCAN_INTERVAL_MS = 1_000;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 60_000;

type Domain = {
    /**
     * Every unresolved due timestamp this domain is waiting on. Doubles as the
     * domain's signature: the earliest entry says when to refresh, and any
     * change to the list means the last refresh made progress.
     */
    dueTimes: () => number[];
    run: () => Promise<unknown>;
};

type DomainRuntime = {
    inFlight: boolean;
    attempts: number;
    nextAllowedAt: number;
    lastSignature: string;
};

function newRuntime(): DomainRuntime {
    return { inFlight: false, attempts: 0, nextAllowedAt: 0, lastSignature: '' };
}

/**
 * The app's single live-state loop, mounted once by `AppShell` so game state
 * keeps advancing on every route rather than only on whichever page happens to
 * own a timer.
 *
 * Two cadences:
 *
 * - a 1 s scan that refreshes a domain the moment something queued in it comes
 *   due (a building finishes, a ship batch delivers, a fleet lands);
 * - a 60 s heartbeat that refreshes everything regardless, so resources keep
 *   accruing and anything the scan can't see still lands eventually.
 *
 * The scan holds no React state on purpose — it reads the stores imperatively
 * via `getState()` and does zero render work, so putting it at the root of the
 * tree costs nothing. Pages still run their own `useNow()`, but purely to
 * render countdowns.
 *
 * Domains back off exponentially while refreshing keeps changing nothing.
 * Page-scoped polling could afford to retry at 1 Hz forever because it died on
 * navigation; app-wide, a checkpoint that never clears would otherwise be a
 * permanent request storm for the rest of the session.
 */
export function useGameLoop(userId: string | undefined, planetId: string | null) {
    const runtimes = useRef<Record<string, DomainRuntime>>({});
    // The planet that is actually loaded, which lags `planetId` (the id we
    // intend to manage) by one fetch.
    const loadedPlanetId = usePlanetStore((s) => s.currentPlanet?.id ?? null);

    // Held in a ref so the intervals don't have to be torn down and rebuilt
    // (losing their backoff state) on every identity change.
    const target = useRef({ userId, planetId });
    target.current = { userId, planetId };

    // Apply elapsed time the moment a planet becomes available — on first
    // load and on every planet switch — rather than leaving the player looking
    // at pre-tick resources until the first heartbeat a minute later.
    useEffect(() => {
        if (!userId || !planetId || !loadedPlanetId) return;
        usePlanetStore.getState().tickCurrentPlanet()
            .catch((err) => console.error('On-load planet tick failed:', err));
        // Deliberately keyed on the loaded planet only: re-running this when
        // `planetId` changes would tick the OLD planet again in the window
        // before the new one arrives.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadedPlanetId]);

    useEffect(() => {
        if (!userId || !planetId) return;

        const refreshPlayerState = () => {
            const { userId: uid, planetId: pid } = target.current;
            if (!uid || !pid) return;
            usePlayerStore.getState().loadPlayerState(uid, pid)
                .catch((err) => console.error('Science refresh failed:', err));
            usePlayerStore.getState().loadShipUpgradeState(uid, pid)
                .catch((err) => console.error('Ship-upgrade refresh failed:', err));
        };

        const heartbeat = () => {
            const { userId: uid } = target.current;
            usePlanetStore.getState().tickCurrentPlanet()
                .catch((err) => console.error('Planet tick failed:', err));
            refreshPlayerState();
            // Only on the heartbeat: AppShell already loads the list on mount
            // (it needs it even before a planet resolves, for the switcher).
            if (uid) {
                usePlanetStore.getState().loadPlanets(uid)
                    .catch((err) => console.error('Planet list refresh failed:', err));
            }
        };

        // Seed player-wide state, and pull the fleet list once so the scan has
        // something to watch. The initial planet tick is deliberately NOT here
        // — the on-load effect above owns it, and firing both would mean two
        // concurrent ticks on every mount. `getFleets` mutates server-side (it
        // processes due arrivals), so it likewise has no place on the
        // heartbeat; the scan resolves arrivals exactly when they come due.
        refreshPlayerState();
        useFleetStore.getState().loadFleets()
            .catch((err) => console.error('Fleet load failed:', err));

        const id = setInterval(heartbeat, HEARTBEAT_MS);
        return () => clearInterval(id);
    }, [userId, planetId]);

    useEffect(() => {
        if (!userId || !planetId) return;

        const domains: Record<string, Domain> = {
            // One domain for all three planet queues: they resolve through the
            // same `/tickPlanet` call, so separate latches would fire
            // concurrent redundant ticks on a planet with more than one kind of
            // batch pending.
            planet: {
                dueTimes: () => planetDueTimesMs(usePlanetStore.getState().currentPlanet),
                run: () => usePlanetStore.getState().tickCurrentPlanet(),
            },
            research: {
                dueTimes: () => researchDueTimesMs(usePlayerStore.getState().tickCheckpoints),
                run: () => {
                    const { userId: uid, planetId: pid } = target.current;
                    return usePlayerStore.getState().loadPlayerState(uid!, pid!);
                },
            },
            shipUpgrade: {
                dueTimes: () => shipUpgradeDueTimesMs(usePlayerStore.getState().shipUpgradeQueue),
                run: () => {
                    const { userId: uid, planetId: pid } = target.current;
                    return usePlayerStore.getState().loadShipUpgradeState(uid!, pid!);
                },
            },
            fleets: {
                dueTimes: () => fleetDueTimesMs(useFleetStore.getState().view?.fleets),
                run: () => useFleetStore.getState().loadFleets(),
            },
        };

        const scan = () => {
            const now = Date.now();
            for (const [key, domain] of Object.entries(domains)) {
                const runtime = (runtimes.current[key] ??= newRuntime());
                if (runtime.inFlight || now < runtime.nextAllowedAt) continue;

                const dueTimes = domain.dueTimes();
                const dueAt = earliestDueMs(dueTimes);
                if (dueAt === null || dueAt > now) continue;

                runtime.inFlight = true;
                runtime.lastSignature = dueTimes.join(',');
                domain.run()
                    .catch((err) => console.error(`Game loop refresh failed (${key}):`, err))
                    .finally(() => {
                        const signature = domain.dueTimes().join(',');
                        runtime.attempts = signature === runtime.lastSignature ? runtime.attempts + 1 : 0;
                        runtime.lastSignature = signature;
                        runtime.nextAllowedAt = Date.now()
                            + Math.min(BACKOFF_BASE_MS * 2 ** runtime.attempts, BACKOFF_MAX_MS);
                        runtime.inFlight = false;
                    });
            }
        };

        const id = setInterval(scan, SCAN_INTERVAL_MS);
        return () => clearInterval(id);
    }, [userId, planetId]);
}
